import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import PageSelect from './PageSelect';
import LocatorTypeSelect from './LocatorTypeSelect';
import { FLAG_MAP, PLACEHOLDER_FLAG } from '../../config/marketFlags';

export default function CreateLocatorModal({
  open,
  form,
  pages,
  markets,
  loading,
  error,
  onClose,
  onSave,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={onSave}>
        <DialogTitle>Create locator</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}
        >
          {error && <Alert severity="error">{error}</Alert>}
          <Controller
            name="market_id"
            control={form.control}
            rules={{ required: 'Required' }}
            render={({ field, fieldState }) => (
              <>
                <Select size="small" displayEmpty {...field} error={Boolean(fieldState.error)}>
                  <MenuItem value="" disabled>
                    Select market
                  </MenuItem>
                  {markets.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <img
                          src={FLAG_MAP[m.id] || PLACEHOLDER_FLAG}
                          alt={`${m.name} flag`}
                          style={{
                            width: 30,
                            height: 30,
                            marginRight: 8,
                            mixBlendMode: 'multiply',
                          }}
                        />
                        {m.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {fieldState.error && (
                  <Typography variant="caption" color="error">
                    {fieldState.error.message}
                  </Typography>
                )}
              </>
            )}
          />
          <Controller
            name="platform"
            control={form.control}
            rules={{ required: 'Required' }}
            render={({ field, fieldState }) => (
              <>
                <Select size="small" displayEmpty {...field} error={Boolean(fieldState.error)}>
                  <MenuItem value="" disabled>
                    Select platform
                  </MenuItem>
                  <MenuItem value="android">Android</MenuItem>
                  <MenuItem value="ios">iOS</MenuItem>
                </Select>
                {fieldState.error && (
                  <Typography variant="caption" color="error">
                    {fieldState.error.message}
                  </Typography>
                )}
              </>
            )}
          />
          <TextField
            label="Build"
            size="small"
            {...form.register('build', {
              required: 'Required',
              validate: (v) => (v !== '' && !isNaN(Number(v))) || 'Must be a number',
            })}
            error={Boolean(form.formState.errors.build)}
            helperText={form.formState.errors.build?.message}
          />
          <Controller
            name="page"
            control={form.control}
            rules={{ required: 'Required' }}
            render={({ field, fieldState }) => (
              <PageSelect
                pages={pages}
                placeholder="Select page"
                disablePlaceholder
                error={fieldState.error}
                {...field}
              />
            )}
          />
          <TextField
            label="Locator name"
            size="small"
            {...form.register('locator_name', { required: 'Required' })}
            error={Boolean(form.formState.errors.locator_name)}
            helperText={form.formState.errors.locator_name?.message}
          />
          <Controller
            name="locator_type"
            control={form.control}
            rules={{ required: 'Required' }}
            render={({ field, fieldState }) => (
              <LocatorTypeSelect error={fieldState.error} {...field} />
            )}
          />
          <TextField
            label="Locator value"
            size="small"
            multiline
            rows={3}
            {...form.register('locator_value', { required: 'Required' })}
            error={Boolean(form.formState.errors.locator_value)}
            helperText={form.formState.errors.locator_value?.message}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={18} /> : 'Create'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
